/**
 * Video utility functions for timestamped comments
 */

export interface VideoComment {
  _id: string;
  comment: string;
  createdAt: Date | string;
  createdBy: {
    _id: string;
    fullName: string;
  };
  itemId: string;
  videoTimestamp?: number; // Timestamp in seconds
  module?: string;
  type?: string;
  userName?: string; 
}

/**
 * Format seconds to MM:SS display format
 * @param seconds - Time in seconds
 * @returns Formatted string like "1:30" or "0:05"
 */
export const formatVideoTimestamp = (seconds: number): string => {
  if (!seconds || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Parse MM:SS string to seconds
 * @param timestamp - String like "1:30"
 * @returns Time in seconds
 */
export const parseVideoTimestamp = (timestamp: string): number => {
  if (!timestamp) return 0;
  const parts = timestamp.split(":");
  if (parts.length !== 2) return 0;
  const [mins, secs] = parts.map(Number);
  if (isNaN(mins) || isNaN(secs)) return 0;
  return mins * 60 + secs;
};

/**
 * Check if a comment is "active" (within threshold of current time)
 * @param commentTimestamp - Comment timestamp in seconds
 * @param currentTime - Current video time in seconds
 * @param threshold - Time window in seconds (default 2)
 * @returns Whether the comment is active
 */
export const isCommentActive = (
  commentTimestamp: number,
  currentTime: number,
  threshold: number = 2
): boolean => {
  if (commentTimestamp === undefined || commentTimestamp === null) return false;
  return Math.abs(commentTimestamp - currentTime) <= threshold;
};

/**
 * Sort comments by timestamp (ascending)
 * @param comments - Array of video comments
 * @returns Sorted array
 */
export const sortCommentsByTimestamp = (
  comments: VideoComment[]
): VideoComment[] => {
  return [...comments].sort(
    (a, b) => (a.videoTimestamp || 0) - (b.videoTimestamp || 0)
  );
};

/**
 * Filter comments that have a video timestamp
 * @param comments - Array of comments
 * @returns Only comments with videoTimestamp defined
 */
export const filterVideoComments = (comments: any[]): VideoComment[] => {
  if (!comments || !Array.isArray(comments)) return [];
  return comments.filter(
    (comment) =>
      comment.videoTimestamp !== undefined && comment.videoTimestamp !== null
  );
};

/**
 * Calculate position percentage for a timestamp on the timeline
 * @param timestamp - Timestamp in seconds
 * @param duration - Total video duration in seconds
 * @returns Percentage (0-100)
 */
export const calculateTimelinePosition = (
  timestamp: number,
  duration: number
): number => {
  if (!duration || duration <= 0) return 0;
  const percentage = (timestamp / duration) * 100;
  return Math.max(0, Math.min(100, percentage));
};

/**
 * Find the closest comment to the current time
 * @param comments - Array of video comments
 * @param currentTime - Current video time in seconds
 * @returns The closest comment or null
 */
export const findClosestComment = (
  comments: VideoComment[],
  currentTime: number
): VideoComment | null => {
  if (!comments || comments.length === 0) return null;

  const videoComments = comments.filter(
    (c) => c.videoTimestamp !== undefined && c.videoTimestamp !== null
  );
  if (videoComments.length === 0) return null;

  return videoComments.reduce((closest, current) => {
    const closestDiff = Math.abs((closest.videoTimestamp || 0) - currentTime);
    const currentDiff = Math.abs((current.videoTimestamp || 0) - currentTime);
    return currentDiff < closestDiff ? current : closest;
  });
};
